/*  iceberg-ui  */
import { Form, FormSelect, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Alert,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Tab,
    TextField,
    Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { PageTitle } from 'application/Shared/Components/App';
import { IdentityManagementIntegrations } from 'application/Shared/Components/Client/IdentityManagementIntegrations';
import { ClientSaasOpsIntegrations } from 'application/Shared/Components/Client/SaasOps/ClientSaasOpsIntegrations';

/* Shared */
import { MUITabsWrapper } from 'application/Shared/Components/MUITabsWrapper';
import { FontSizes } from 'application/Shared/Constants';
import { Route } from 'application/Shared/Helpers';
import { useGetClientLinks } from 'hooks/dataAccess/ClientLinks/clientLink';
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import PartnerIntegrations from './PartnerIntegrations';

const ClientIntegrationsUI = ({ clientLinks, loading, refetchClientData }) => {
    const [searchParams] = useSearchParams();
    const urlClientId = searchParams.get('clientId');
    const collapseParam = searchParams.get('collapse');

    const isSaasOpsExpanded = collapseParam !== 'saasOpsIntegrations';
    const isIdentityManagementExpanded =
        collapseParam !== 'identityManagementIntegrations';

    const clientOptions = clientLinks
        ?.map(({ tenant }) => ({
            label: tenant.name,
            value: tenant.id
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const hasClients = !!clientOptions?.length;

    const firstClientId = clientOptions[0]?.value;
    const [clientId, setClientId] = useState(firstClientId);
    const [filter, setFilter] = useState('');

    const clientLink = clientLinks?.find(
        ({ tenant }) => tenant.id === clientId
    );

    const { t } = useTranslation();
    const { partnerId } = useParams();

    // on initial load, clients may just be an empty array, and clientId may get set to `undefined` which means
    // the select box has an improper state at first.
    useEffect(() => {
        if (!clientId && firstClientId) {
            setClientId(firstClientId);
        }
    }, [firstClientId, clientId]);

    // Set the client ID from URL parameters when provided
    useEffect(() => {
        if (urlClientId && clientOptions?.length) {
            // Check if the URL clientId exists in our client options
            const clientExists = clientOptions.some(
                (option) => option.value === urlClientId
            );
            if (clientExists) {
                setClientId(urlClientId);
            }
        }
    }, [urlClientId, clientOptions]);

    if (loading) {
        return (
            <Card variant="outlined" style={{ overflow: 'visible' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                    <CircularProgress />
                </CardContent>
            </Card>
        );
    }

    const showIdentityManagement = !!clientLink?.tenant;

    return (
        <>
            <Card variant="outlined" style={{ overflow: 'visible' }}>
                <CardContent>
                    {!hasClients && (
                        <Box marginBottom="16px">
                            <Alert severity="warning" icon={false}>
                                <Typography>
                                    <Trans
                                        i18nKey="Integrations.noClientsFound"
                                        components={{
                                            firstLink: (
                                                <Link
                                                    to={`/partner/${partnerId}/organizations`}
                                                    className="link--tertiary"
                                                    style={{
                                                        fontWeight: '600'
                                                    }}
                                                    data-trackid="partner_integrations_organizations_routeTo"
                                                />
                                            ),
                                            secondLink: (
                                                <Link
                                                    to={`/partner/${partnerId}/integrations/partnerLevel`}
                                                    className="link--tertiary"
                                                    style={{
                                                        fontWeight: '600'
                                                    }}
                                                    data-trackid="partner_integrations_partnerLevel_routeTo"
                                                />
                                            )
                                        }}
                                    />
                                </Typography>
                            </Alert>
                        </Box>
                    )}
                    <Box sx={{ maxWidth: '500px' }}>
                        <Form>
                            <FormSelect
                                key={clientId} // Force re-render when clientId changes
                                data-testid="dropdownMenu"
                                label={t('Common.client')}
                                name="clientId"
                                onChange={({ value }) => {
                                    setClientId(value);
                                }}
                                options={clientOptions}
                                placeholder="Client"
                                value={clientId || ''}
                                sx={{
                                    alignItems: 'center',
                                    display: 'flex'
                                }}
                                readOnly={!hasClients}
                            />
                        </Form>
                    </Box>
                </CardContent>
            </Card>
            <Card variant="outlined" sx={{ marginTop: '16px' }}>
                <CardContent>
                    <Box marginBottom="16px">
                        <TextField
                            disabled={!hasClients}
                            autoComplete="off"
                            sx={{ width: '40%' }}
                            InputProps={{
                                startAdornment: (
                                    <IconWithVariant
                                        variant="search"
                                        src="Search"
                                        size={FontSizes[1]}
                                        wrapperProps={{
                                            marginRight: '4px'
                                        }}
                                    />
                                )
                            }}
                            onChange={(event) => {
                                const newValue = event.target.value;
                                setFilter(newValue);
                            }}
                            placeholder={t('Common.search')}
                            variant="outlined"
                            size="small"
                        />
                    </Box>
                    {clientId && (
                        <ClientSaasOpsIntegrations
                            clientId={clientId}
                            disableAllCards={!hasClients}
                            accordion
                            filter={filter}
                            defaultExpanded={isSaasOpsExpanded}
                        />
                    )}

                    {showIdentityManagement && (
                        <Box mt="16px">
                            <IdentityManagementIntegrations
                                clientTenant={clientLink?.tenant}
                                providers={clientLink?.provider}
                                filter={filter}
                                isCollapsible
                                refetchClientData={refetchClientData}
                                defaultExpanded={isIdentityManagementExpanded}
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
        </>
    );
};

const ClientIntegrations = () => {
    const params = useMemo(
        () => ({
            hidden: false,
            includeProvider: true,
            includeTenant: true,
            size: -1
        }),
        []
    );
    const { clientLinks, loading, refetchData } = useGetClientLinks(params);

    return (
        <Box>
            <ClientIntegrationsUI
                loading={loading}
                clientLinks={clientLinks}
                refetchClientData={refetchData}
            />
        </Box>
    );
};

export const PartnerIntegrationsDashboard = () => {
    const { t } = useTranslation();
    const session = useSession();
    const { partnerId, tab } = useParams();
    const activeTab = tab ?? 'partnerLevel';

    const basePartnerRoute = Route.buildBasePartnerRoute({ partnerId });

    return (
        <>
            <PageTitle title={t('Common.integrations')} />
            <Grid container spacing={3} data-testid="integrationsTabs">
                <Grid item xs={12}>
                    <MUITabsWrapper value={activeTab}>
                        <Tab
                            component={Link}
                            to={`${basePartnerRoute}/integrations/partnerLevel`}
                            value="partnerLevel"
                            label={t('Common.partner')}
                            data-testid="partnerTab"
                            data-trackid="partner_integrations_partner"
                        />
                        <Tab
                            component={Link}
                            to={`${basePartnerRoute}/integrations/clientLevel`}
                            value="clientLevel"
                            label={t('Common.client')}
                            data-testid="clientTab"
                            data-trackid="partner_integrations_client"
                        />
                    </MUITabsWrapper>
                </Grid>
            </Grid>

            {activeTab === 'clientLevel' ? (
                <ClientIntegrations />
            ) : (
                <PartnerIntegrations session={session} />
            )}
        </>
    );
};
